'use client';
// Journal d'une fiche de registre — non-conformité, réclamation, suggestion,
// action, risque.
//
// ── Pourquoi ce composant existe ────────────────────────────────────────────
//
// La RQ a demandé le 14/08/2026 : « Il n'y a pas de journal sur l'évolution
// d'une suggestion du personnel. Pourquoi ? Dans toutes les actions il me faut
// un journal où je peux voir les détails de ce qui s'est passé pour les moments
// clés de la vie d'une fiche. »
//
// La réponse est instructive : le journal EXISTAIT DÉJÀ. Les dix-neuf
// événements des cinq suggestions étaient écrits, chaînés et opposables en
// audit depuis le premier jour — `appendEvent` est appelé au dépôt, à chaque
// traitement et au passage au plan. Ce qui manquait, c'était l'ÉCRAN. Une
// preuve qu'on ne peut pas montrer ne vaut pas comme preuve : l'auditeur qui
// demande « montrez-moi l'historique de cette suggestion » n'a pas accès à la
// base, il a accès à cet écran.
//
// D'où un composant PARTAGÉ plutôt qu'un bloc recopié dans chaque registre :
// la fiche document avait le sien depuis longtemps, et le recopier une
// quatrième fois aurait garanti que les quatre divergent.
//
// ── Ce qu'il ne fait pas ────────────────────────────────────────────────────
//
// Il ne RÉÉCRIT rien et n'offre aucune correction : le journal est en ajout
// seul et chaîné (l'ordre de lecture EST la chaîne, cf. `loadJournal`). Un
// journal éditable ne serait pas un journal.
import React from 'react';
import { useGed } from '@/lib/store';
import { I } from '@/components/ui';
import { fmtDateTime } from '@/lib/meta';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ctx = any;

/** Libellés lisibles des moments clés. Un `evType` est une clé technique
 *  (`SuggestionPorteeAuPlan`) : la laisser à l'écran demande au lecteur de
 *  décoder du CamelCase pendant un audit. Les clés inconnues retombent sur une
 *  césure automatique — mieux vaut « Suggestion Portee Au Plan » qu'un vide. */
const LIBELLE: Record<string, string> = {
  // Auto-évaluation de paillasse — libellé explicite : lu dans un journal, à
  // côté d'événements d'audit, « AutoEvaluationEnregistree » se confondrait
  // avec un acte d'audit interne, ce qu'il n'est pas (§8.8).
  AutoEvaluationEnregistree: 'Auto-évaluation de paillasse enregistrée',
  // Non-conformités
  Declaree: 'Non-conformité déclarée',
  PriseEnCharge: 'Prise en charge',
  TraitementEnregistre: 'Traitement enregistré',
  NCCloturee: 'Non-conformité clôturée',
  NCFicheModifiee: 'Fiche corrigée',
  // Réclamations
  ReclamationRecue: 'Réclamation reçue',
  ReclamationAnalyse: 'Analyse de la réclamation',
  ReclamationReponse: 'Réponse au réclamant',
  ReclamationReponseModifiee: 'Réponse corrigée',
  ReclamationCloturee: 'Réclamation clôturée',
  // Suggestions du personnel
  SuggestionDeposee: 'Suggestion déposée',
  SuggestionTraitee: 'Traitement de la suggestion',
  SuggestionPorteeAuPlan: 'Portée au plan d’actions',
  // Actions (CAPA)
  ActionCreee: 'Action créée',
  ActionDefinie: 'Action définie',
  ActionDemarree: 'Action démarrée',
  ActionRealisee: 'Action réalisée',
  ActionVerifiee: 'Efficacité vérifiée',
  ActionReplanifiee: 'Action replanifiée',
  ActionAnnulee: 'Action annulée',
  /* Les trois maillons de la reprise du 11/10/2026 (purge des actions ouvertes
     d'office). Ils ne naissent pas d'un geste d'écran et ne se reverront pas —
     mais ils restent au journal pour toujours, et un libellé lisible vaut mieux
     que le repli en CamelCase découpé. */
  ActionRetireeDuPlan: 'Action retirée du plan',
  ActionRenumerotee: 'Référence changée',
  GraviteCorrigee: 'Gravité corrigée',
  // Documents — archivage d'une version (06/10/2026). Le mot « instantané » ne
  // dit rien à un lecteur de journal : ce qu'il doit comprendre, c'est qu'un
  // exemplaire de cette version est conservé et reste consultable.
  VersionArchivee: 'Exemplaire de la version conservé',
  // Liens entre fiches
  LienCree: 'Lien créé',
  LienRetire: 'Lien retiré',
};

const lisible = (t: string) => LIBELLE[t] || String(t).replace(/([a-z])([A-Z])/g, '$1 $2');

export function JournalFiche({ code, titre, compact, evenements }: {
  /** Référence de la fiche — c'est la clé du journal (`ged_events.docCode`). */
  code: string;
  titre?: string;
  /** Rendu resserré, pour un panneau latéral ou une modale. */
  compact?: boolean;
  /** Journal DÉJÀ chargé par l'appelant. Les fiches NC, réclamation et action
   *  le lisent elles-mêmes pour compter leur onglet ; le repasser ici évite une
   *  seconde requête pour les mêmes lignes. Absent = ce composant le charge. */
  evenements?: Ctx[];
}) {
  const { loadJournal, events } = useGed();
  const fourni = Array.isArray(evenements);
  const [charge, setCharge] = React.useState(fourni);

  React.useEffect(() => {
    if (!code || fourni) return;
    void loadJournal(code).finally(() => setCharge(true));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [code, fourni]);

  const journal = fourni ? (evenements as Ctx[]) : (events[code] || []);

  return (
    <section style={{ display: 'grid', gap: 8 }}>
      {/* Le même glyphe que sur le bouton d'accès : une fois appris sur un
          registre, il désigne le journal PARTOUT où celui-ci s'affiche. */}
      {titre !== '' && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          <I n="journal" s={12} />
          {titre || `Journal (${journal.length})`}
        </div>
      )}

      {/* Un journal vide et un journal PAS ENCORE CHARGÉ se ressemblent à
          l'écran et ne veulent pas du tout dire la même chose : le premier est
          un constat, le second une attente. Ils sont donc distingués. */}
      {!charge && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '10px 0' }}>Lecture du journal…</div>
      )}

      {charge && journal.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, padding: '10px 12px', background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)' }}>
          Aucun événement enregistré pour <strong>{code}</strong>. Une fiche déposée depuis la mise
          en service en porte toujours au moins un — un journal vide signale une fiche reprise
          d’avant le journal, pas une fiche sans histoire.
        </div>
      )}

      {charge && journal.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: compact ? 0 : 1 }}>
          {journal.map((e, i) => (
            <li
              key={e.chainHash || `${e.evType}-${i}`}
              style={{
                display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                padding: compact ? '8px 0' : '10px 12px',
                borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                background: compact ? 'none' : (i % 2 ? 'var(--gray-25)' : 'transparent'),
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', minWidth: 116 }}>
                {fmtDateTime(e.ts)}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-800)', minWidth: 168 }}>
                {lisible(e.evType)}
              </span>
              {/* Le détail est du texte écrit par le serveur au moment de
                  l'acte : il n'est jamais recalculé à la lecture, sans quoi le
                  journal raconterait l'état d'aujourd'hui et non ce qui s'est
                  passé ce jour-là. */}
              <span style={{ flex: 1, minWidth: 180, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                {e.payload}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{e.actorName}</span>
              {e.signature && (
                <span
                  title="Signature du maillon — le journal est chaîné : un événement retiré ou modifié casse la chaîne."
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--color-primary-active)', background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)', padding: '2px 7px' }}
                >{e.signature}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
